export const USAGE_WARNING_THRESHOLD = 0.8;

export const secToMin = (sec) => Math.round((sec / 60) * 10) / 10;

export const fmtResetDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const weekdayFromEndDate = (endDate) => {
  if (!endDate) return "Monday";
  const d = endDate instanceof Date ? endDate : new Date(endDate);
  if (Number.isNaN(d.getTime())) return "Monday";
  return d.toLocaleDateString("en-US", { weekday: "long" });
};

const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_TRIAL_DAYS = 7;

const isTruthyFlag = (v) =>
  v === true || v === 1 || /^(true|1|yes)$/i.test(String(v ?? "").trim());

const toDate = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

// The subscription record comes straight from the upstream API, so trial
// fields are read defensively. The Windows app declares IsTrialPeriod on the
// same record; a Stripe trial reports Status "trialing". When no explicit
// trial end date is present the current period end (EndDate) is used.
const deriveTrial = (raw, endDate) => {
  const isTrial =
    isTruthyFlag(raw.IsTrialPeriod) ||
    String(raw.Status || "").trim().toLowerCase() === "trialing";
  if (!isTrial) {
    return { isTrial: false, trialEndsOn: null, trialDaysLeft: null, trialLengthDays: null };
  }
  const trialEndsOn = toDate(raw.TrialEndsOn) || toDate(raw.TrialEndDate) || endDate || null;
  const trialStart = toDate(raw.TrialStartDate) || toDate(raw.StartDate);
  const trialLengthDays =
    trialEndsOn && trialStart && trialEndsOn > trialStart
      ? Math.round((trialEndsOn - trialStart) / DAY_MS)
      : DEFAULT_TRIAL_DAYS;
  const trialDaysLeft = trialEndsOn
    ? Math.max(0, Math.ceil((trialEndsOn.getTime() - Date.now()) / DAY_MS))
    : null;
  return { isTrial: true, trialEndsOn, trialDaysLeft, trialLengthDays };
};

export const deriveUsageState = (raw) => {
  if (!raw) return null;
  const isPro = String(raw.SubscriptionType || "").trim() === "Pro";
  const usedMin = secToMin(raw.UtilizeSecond || 0);
  const totalMin = isPro
    ? null
    : raw.MinuteFree > 0
      ? raw.MinuteFree
      : secToMin(raw.FreeSecond || 0);
  const remainingMin =
    totalMin !== null ? Math.round(Math.max(totalMin - usedMin, 0) * 10) / 10 : null;
  // Second-precise counterparts of the minute figures above. The minute values
  // are rounded to 0.1 for display; the recorder's hard cut-off needs the
  // exact remaining budget so a Free user can never run past it.
  const usedSec = Math.max(0, Number(raw.UtilizeSecond) || 0);
  const totalSec = isPro
    ? null
    : raw.MinuteFree > 0
      ? raw.MinuteFree * 60
      : Math.max(0, Number(raw.FreeSecond) || 0);
  const remainingSec =
    totalSec !== null ? Math.max(0, Math.floor(totalSec - usedSec)) : null;
  const resetsOn = fmtResetDate(raw.EndDate);
  const planLabel = isPro
    ? `ZT Pro${raw.SubscriptionPeriod ? ` (${raw.SubscriptionPeriod})` : ""}`
    : "Free Plan";
  const balanceDays = raw.BalanceDays ?? null;
  const endDate = raw.EndDate ? new Date(raw.EndDate) : null;
  const isActive = String(raw.Status || "").trim() === "Active";
  const trial = deriveTrial(raw, endDate);
  return {
    ...trial,
    isPro,
    isActive,
    usedMin,
    totalMin,
    remainingMin,
    usedSec,
    totalSec,
    remainingSec,
    resetsOn,
    planLabel,
    balanceDays,
    endDate,
    subscriptionPeriod: raw.SubscriptionPeriod || "",
  };
};

/**
 * A seat in a team in good standing gives Pro, while the subscription record
 * still shows the person's own plan (often Free): no free-minute limit then.
 */
export const withTeamSeat = (usage, teamName) => {
  if (!usage || !teamName) return usage;
  return {
    ...usage,
    isPro: true,
    isTeam: true,
    totalMin: null,
    remainingMin: null,
    totalSec: null,
    remainingSec: null,
    planLabel: `ZT Teams · ${teamName}`,
    // The team pays: no personal-trial countdown or "Keep Pro" nudges.
    isTrial: false,
    trialEndsOn: null,
    trialDaysLeft: null,
    trialLengthDays: null,
  };
};

export const computeBannerState = (usage) => {
  if (!usage) return null;
  if (usage.isPro) return null;
  if (usage.totalMin == null || usage.totalMin <= 0) return null;

  const ratio = usage.usedMin / usage.totalMin;
  if (ratio < USAGE_WARNING_THRESHOLD) return null;

  return {
    severity: ratio >= 1 ? "limit-reached" : "approaching",
    usedMinutes: Math.round(usage.usedMin),
    totalMinutes: Math.round(usage.totalMin),
    resetDay: weekdayFromEndDate(usage.endDate),
  };
};
